import { IGenericTableData } from '@/interfaces/powerMarkets/table/IGenericTableData.ts';
import { IGenericTableHeaderRow } from '@/interfaces/powerMarkets/table/IGenericTableHeaderRow.ts';
import {
    IReportCsvSection,
    IReportExportProvider,
    IReportReadyResult,
    IReportWorkbookCollectionResult,
    IReportWorkbookMetadata,
    IReportWorkbookSheet,
    TReportExportCell,
} from '@/interfaces/reports/IReportExport.ts';
import * as XLSX from 'xlsx';

const EXCEL_SHEET_NAME_MAX_LENGTH = 31;
const EXCEL_INVALID_SHEET_NAME_CHARACTERS = /[:\\/?*[\]]/g;
const EXCEL_FORMULA_TRIGGER = /^[=+\-@]/;

export function escapeCsvCell (cell: TReportExportCell): string {
    if (cell === null || cell === undefined) return '';

    const text = String(cell);
    const safeText = EXCEL_FORMULA_TRIGGER.test(text) ? `'${text}` : text;
    if (!/[",\r\n]/.test(safeText)) return safeText;

    return `"${safeText.replace(/"/g, '""')}"`;
}

export function serialiseReportCsvSections (sections: Array<IReportCsvSection>): string {
    const rows: Array<Array<TReportExportCell>> = [];

    sections.forEach((section, index) => {
        if (index > 0) rows.push([]);
        rows.push([section.title]);
        rows.push(...section.rows);
    });

    return `\uFEFF${rows.map(row => row.map(escapeCsvCell).join(',')).join('\r\n')}\r\n`;
}

function sheetHasData (sheet: IReportWorkbookSheet): boolean {
    return sheet.rows.some(row => row.some(cell => cell !== null && cell !== undefined && cell !== ''));
}

function normaliseReadyValue (readyValue: unknown): IReportReadyResult {
    if (typeof readyValue === 'boolean') {
        return readyValue
            ? { status: 'ready' }
            : { status: 'loading', reason: 'Module is still loading' };
    }

    if (
        typeof readyValue === 'object'
        && readyValue
        && 'status' in readyValue
        && ['ready', 'loading', 'failed', 'skipped'].includes(String((readyValue as IReportReadyResult).status))
    ) {
        return readyValue as IReportReadyResult;
    }

    return { status: 'ready' };
}

export function resolveReportProviderReady (provider: IReportExportProvider): IReportReadyResult {
    const ready = provider.ready;
    if (typeof ready === 'function') return normaliseReadyValue(ready());
    if (typeof ready === 'object' && ready && 'value' in ready) return normaliseReadyValue(ready.value);
    return normaliseReadyValue(ready);
}

export function sanitiseWorkbookSheetTitle (title: string): string {
    const cleaned = title
        .replace(EXCEL_INVALID_SHEET_NAME_CHARACTERS, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^'+|'+$/g, '');

    return (cleaned || 'Sheet').slice(0, EXCEL_SHEET_NAME_MAX_LENGTH);
}

export function getUniqueWorkbookSheetTitle (title: string, existingTitles: Set<string>): string {
    const sanitised = sanitiseWorkbookSheetTitle(title);
    let candidate = sanitised;
    let index = 2;

    while (existingTitles.has(candidate.toLowerCase())) {
        const suffix = ` (${index})`;
        candidate = `${sanitised.slice(0, EXCEL_SHEET_NAME_MAX_LENGTH - suffix.length)}${suffix}`;
        index += 1;
    }

    existingTitles.add(candidate.toLowerCase());
    return candidate;
}

export async function collectReportWorkbookSheetsWithDiagnostics (providers: Array<IReportExportProvider>): Promise<IReportWorkbookCollectionResult> {
    const results = await Promise.allSettled(
        providers.map(async provider => {
            const readiness = resolveReportProviderReady(provider);
            if (readiness.status !== 'ready') {
                return {
                    provider,
                    sheets: [],
                    reason: readiness.reason ?? `Module is ${readiness.status}`,
                    severity: readiness.status === 'skipped' ? 'info' as const : 'warning' as const,
                };
            }

            const sheets = provider.getWorkbookSheets
                ? await provider.getWorkbookSheets()
                : await provider.getCsvSections();
            const exportableSheets = sheets.filter(sheetHasData);

            return {
                provider,
                sheets: exportableSheets,
                reason: exportableSheets.length ? '' : 'No workbook data',
                severity: 'info' as const,
            };
        }),
    );

    const collectionResult: IReportWorkbookCollectionResult = {
        sheets: [],
        skippedProviders: [],
    };

    results.forEach((result, index) => {
        const provider = providers[index];
        if (result.status === 'rejected') {
            collectionResult.skippedProviders.push({
                moduleId: provider.moduleId,
                title: provider.title,
                kind: provider.kind,
                reason: result.reason instanceof Error ? result.reason.message : 'Unable to collect workbook data',
                severity: 'warning',
            });
            return;
        }

        collectionResult.sheets.push(...result.value.sheets);
        if (result.value.reason) {
            collectionResult.skippedProviders.push({
                moduleId: provider.moduleId,
                title: provider.title,
                kind: provider.kind,
                reason: result.value.reason,
                severity: result.value.severity,
            });
        }
    });

    return collectionResult;
}

function normaliseWorkbookCell (cell: TReportExportCell): TReportExportCell {
    if (typeof cell !== 'string') return cell;
    if (!EXCEL_FORMULA_TRIGGER.test(cell)) return cell;
    return `'${cell}`;
}

function normaliseWorkbookRows (rows: Array<Array<TReportExportCell>>): Array<Array<TReportExportCell>> {
    return rows.map(row => row.map(normaliseWorkbookCell));
}

function buildWorksheetRows (sheet: IReportWorkbookSheet): Array<Array<TReportExportCell>> {
    const rows: Array<Array<TReportExportCell>> = [
        [sheet.title],
    ];

    if (sheet.subtitle) {
        rows.push([sheet.subtitle]);
    }

    if (sheet.metadataRows?.length) {
        rows.push(
            [],
            ...sheet.metadataRows.map(([label, value]) => [label, value]),
        );
    }

    if (rows.length > 1) rows.push([]);
    rows.push(...sheet.rows);
    return rows;
}

function applyWorksheetPresentation (worksheet: XLSX.WorkSheet, sheet: IReportWorkbookSheet, rows: Array<Array<TReportExportCell>>): void {
    const maxColumnCount = Math.max(1, ...rows.map(row => row.length));
    const columnWidths = Array.from({ length: maxColumnCount }, (_, index) => ({
        wch: sheet.columnWidths?.[index] ?? Math.min(
            48,
            Math.max(12, ...rows.map(row => String(row[index] ?? '').length + 2)),
        ),
    }));

    worksheet['!cols'] = columnWidths;

    const headerOffset = rows.length - sheet.rows.length;
    const tableHeaderRowIndex = sheet.tableHeaderRowIndex === undefined
        ? undefined
        : headerOffset + sheet.tableHeaderRowIndex;

    if (tableHeaderRowIndex !== undefined && rows[tableHeaderRowIndex]) {
        const range = XLSX.utils.decode_range(worksheet['!ref'] ?? 'A1:A1');
        range.s.r = tableHeaderRowIndex;
        range.e.r = Math.max(tableHeaderRowIndex, range.e.r);
        worksheet['!autofilter'] = { ref: XLSX.utils.encode_range(range) };
    }

    if (sheet.freezeHeaderRows) {
        (worksheet as XLSX.WorkSheet & { '!freeze'?: { xSplit: number; ySplit: number } })['!freeze'] = {
            xSplit: 0,
            ySplit: sheet.freezeHeaderRows,
        };
    }
}

function buildWorkbookSummarySheet (metadata: IReportWorkbookMetadata, exportedSheetCount: number): IReportWorkbookSheet {
    const rows: Array<Array<TReportExportCell>> = [
        ['Dashboard', metadata.dashboardName],
        ['Exported at', metadata.exportedAt.toISOString()],
        ['Report modules checked', metadata.providerCount],
        ['Worksheets exported', exportedSheetCount],
        ['Generated by', 'Spark by LCP Delta'],
    ];

    if (metadata.skippedProviders.length) {
        rows.push(
            [],
            ['Skipped modules'],
            ['Module', 'Type', 'Severity', 'Reason'],
            ...metadata.skippedProviders.map(provider => [provider.title, provider.kind, provider.severity, provider.reason]),
        );
    }

    return {
        title: 'Export summary',
        subtitle: 'Spark by LCP Delta dashboard workbook',
        columnWidths: [32, 42, 16, 64],
        freezeHeaderRows: 1,
        tableHeaderRowIndex: metadata.skippedProviders.length ? 8 : undefined,
        rows,
    };
}

export function buildReportWorkbook (sheets: Array<IReportWorkbookSheet>, metadata?: IReportWorkbookMetadata): XLSX.WorkBook | null {
    const workbookSheets = sheets.filter(sheetHasData);
    if (!workbookSheets.length) return null;

    const workbook = XLSX.utils.book_new();
    workbook.Props = {
        Title: metadata?.dashboardName ?? 'Spark dashboard workbook',
        Subject: 'Spark dashboard report export',
        Author: 'Spark by LCP Delta',
        Company: 'LCP Delta',
        CreatedDate: metadata?.exportedAt ?? new Date(),
    };
    const sheetNames = new Set<string>();
    const sheetsToWrite = metadata
        ? [buildWorkbookSummarySheet(metadata, workbookSheets.length), ...workbookSheets]
        : workbookSheets;

    sheetsToWrite.forEach((sheet) => {
        const rows = normaliseWorkbookRows(buildWorksheetRows(sheet));
        const worksheet = XLSX.utils.aoa_to_sheet(rows);
        applyWorksheetPresentation(worksheet, sheet, rows);
        const sheetName = getUniqueWorkbookSheetTitle(sheet.title, sheetNames);
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });

    return workbook;
}

export function downloadReportWorkbook (sheets: Array<IReportWorkbookSheet>, fileName: string, metadata?: IReportWorkbookMetadata): boolean {
    const workbook = buildReportWorkbook(sheets, metadata);
    if (!workbook) return false;
    XLSX.writeFile(workbook, `${fileName}.xlsx`, { compression: true });
    return true;
}

function isLeafHeader (header: IGenericTableData['headerFirstRowById'][string]): header is IGenericTableHeaderRow {
    return !header.isHeaderParent;
}

export function genericTableDataToCsvRows (tableData: IGenericTableData): Array<Array<TReportExportCell>> {
    const tableHeaders = tableData.headerSecondRow?.length
        ? tableData.headerSecondRow
        : Object.values(tableData.headerFirstRowById)
            .filter(isLeafHeader);

    const headers = tableHeaders
        .filter(header => !header.noExcel)
        .map(header => ({ id: header.id, title: header.title || header.id }));

    const rows: Array<Array<string | number | null | undefined>> = [headers.map(header => header.title)];

    tableData.data.forEach(row => {
        rows.push(headers.map(header => {
            const cell = row.row[header.id];
            if (!cell) return '';
            if ('value' in cell && cell.value !== undefined) {
                if (cell.value === null || typeof cell.value === 'string' || typeof cell.value === 'number') return cell.value;
                return String(cell.value);
            }
            if (cell.values?.length) return cell.values.join(', ');
            return '';
        }));
    });

    return rows;
}
